// src/pages/HomePage.jsx
import React, { useState, useMemo } from "react";
import { Bar, Pie, Line, Radar, Scatter } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js/auto";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";

import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { BUTTON_VARIANTS } from "../theme/brandTheme";
import { playedDates } from "../utils/dashboardDates";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
  ChartDataLabels,
  MatrixController,
  MatrixElement
);

// TO BE NUMBER ONE ESPORTS Theme Variables
const theme = {
  // TO BE NUMBER ONE ESPORTS Theme - brand red on light
  bg: "#f7f8fb",
  bgDark: "#eef1f6",
  bgCard: "rgba(255, 255, 255, 0.97)",
  bgGlass: "rgba(255, 16, 38, 0.05)",

  // Primary - Orange
  primary: "#ff1026",
  primaryLight: "#ff4d5f",
  primaryDark: "#a30013",
  primaryGlow: "rgba(255,16,38,0.5)",
  primarySoft: "rgba(255,16,38,0.15)",

  // Secondary accents
  secondary: "#111827",
  secondaryGlow: "rgba(17,24,39,0.12)",
  white: "#ffffff",
  whiteGlow: "rgba(255, 255, 255, 0.3)",

  // Status
  success: "#10b981",
  successGlow: "rgba(0, 255, 136, 0.4)",
  danger: "#f43f5e",
  dangerGlow: "rgba(255, 51, 102, 0.4)",

  // Enemy accent
  enemy: "#f43f5e",
  enemyGlow: "rgba(255, 45, 45, 0.4)",

  // Text
  text: "#111827",
  textDim: "#4b5563",
  textMuted: "#6b7280",

  // Borders
  border: "rgba(255,16,38,0.3)",
  borderDim: "rgba(255, 16, 38, 0.18)",

  // Chart specific
  blue: "#1976d2",
  red: "#ff2d2d",
  accent: "#ff4d5f",
  tag: "rgba(255,16,38,0.2)",

  // Gradients
  bgGradient: "linear-gradient(180deg, #ffffff 0%, #fff3f5 55%, #ffe9ed 100%)",
  purpleGradient: "linear-gradient(135deg, #ff1026 0%, #ff4d5f 100%)",
  tealGradient: "linear-gradient(135deg, #ffb3bc 0%, #ff4d5f 100%)",

  // Shadows
  shadow: "0 8px 24px rgba(255,16,38,0.12)",
  glow: "0 0 16px 3px rgba(255,16,38,0.10)",
  chartGlow: "0 0 16px rgba(255,16,38,0.10)",
  chartBorder: "1px solid rgba(255,16,38,0.25)",

  // Table
  tableHeader: "rgba(255,16,38,0.15)",
  tableHeaderBorder: "2.5px solid rgba(255,16,38,0.4)",
};

const icons = {
  win: "✅",
  lose: "❌",
  fire: "🔥",
  blue: "🔵",
  red: "🔴",
  hero: "🦸‍♂️",
  clock: "⏰",
  chart: "📈",
  trend: "📊",
  trophy: "🏆",
  sword: "⚔️",
  timer: "⏱️",
  search: "🔍",
  filter: "⚙️",
  stats: "📊",
  team: "👥",
  calendar: "📅",
  menu: "☰",
  scan: "📡",
  radar: "🌀",
  warning: "⚠️",
  target: "🎯",
  medal: "🥇",
  crown: "👑",
};

// --- Priority & Tier map ---
const PRIORITY_TEAMS = ["EA", "BAC", "BRU", "TLN"];

const TEAM_TIER_MAP = {
  // Tier 1
  BAC: 1, BRU: 1, EA: 1, FS: 1, FW: 1,
  // Tier 2
  ANK: 2, BMG: 2, DCG: 2, FPT: 2, HKA: 2, ONE: 2, SGP: 2, TALONA: 2, TLN: 2,
  // Tier 3
  "1S": 3, KOG: 3, PSG: 3, TDT: 3,
  // Tier 4
  BACD: 4, BOX: 4, BSS: 4, CMES: 4, FPL: 4, FZ: 4, HD: 4, "Old Man's": 4, RK7: 4, SPN: 4,
};

// --- helpers for Week ---
function normalizeWeekValue(w) {
  const s = String(w ?? "").trim();
  if (!s) return "";
  const m = s.match(/^w?\s*(\d+)$/i);
  if (m) return `W${Number(m[1])}`;
  return s;
}

function getWeekOfGame(g) {
  const raw = g?.week ?? g?.Week ?? "";
  return normalizeWeekValue(raw) || "";
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return "";
  let s = String(dateStr).replace(".0", "");
  if (s.length !== 8) return s;
  return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
}

function formatDateShort(raw) {
  if (raw == null) return "ไม่พบข้อมูล";
  let s = String(raw);
  if (s.endsWith(".0")) s = s.slice(0, -2);
  if (s.length !== 8) return "ไม่พบข้อมูล";
  const yyyy = s.slice(0, 4);
  const mm = s.slice(4, 6);
  const dd = s.slice(6, 8);
  return `${dd}/${mm}/${yyyy.slice(2)}`;
}

function formatTime(t) {
  if (!t || t === "" || isNaN(Number(t))) return "-";
  return Number(t).toFixed(1);
}

function formatDateTimeDisplay(raw) {
  const s = String(raw || "").replace(".0", "");
  if (s.length >= 8) {
    const yyyy = s.slice(0, 4);
    const mm = s.slice(4, 6);
    const dd = s.slice(6, 8);
    if (yyyy && mm && dd) return `${dd}/${mm}/${yyyy}`;
  }
  return "-";
}

function getMatchResult(matchValue) {
  if (!matchValue)
    return { label: "ไม่พบข้อมูล", color: theme.textMuted, status: "unknown", icon: "❓" };
  if (String(matchValue).toUpperCase() === "FS")
    return { label: "Win", color: theme.success, status: "win", icon: icons.win };
  return { label: "Loss", color: theme.danger, status: "lose", icon: icons.lose };
}

// Prefer the parser's resolved win flag (built from the 0/1 FS-Win column with a
// Match Win fallback — same rule the Team Performance report uses) so the Home
// win rate matches it. Fall back to the raw Match Win cell for legacy rows.
function isFsWin(g) {
  if (g && g.win != null) return Number(g.win) === 1;
  return String(g?.match || g?.["Match Win"] || "").toUpperCase() === "FS";
}

const tierOrder = [
  "BACD", "BOX", "BSS", "CMES", "FPL", "FZ", "HD", "Old Man's", "RK7", "SPN",
  "1S", "KOG", "PSG", "TDT",
  "ANK", "BMG", "DCG", "FPT", "HKA", "ONE", "SGP", "TALONA", "TLN",
  "BAC", "BRU", "EA", "FS", "FW",
];

const matchBgColors = [
  "rgba(255,16,38,0.05)",
  "rgba(255, 45, 45, 0.05)",
  "rgba(0, 255, 136, 0.05)",
  "rgba(245,245,245,0.05)",
  "rgba(255, 255, 255, 0.03)",
  "rgba(255,16,38,0.08)",
];

const heroColors = [
  theme.primary,
  theme.secondary,
  theme.success,
  theme.danger,
  theme.enemy,
];

function hexToRgba(hex, alpha = 0.7) {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toYYYYMMDD(dateStr) {
  if (!dateStr) return "";
  let s = String(dateStr).replace(/[^\d]/g, "");
  if (s.length !== 8) return s;
  if (parseInt(s.slice(0, 2)) > 31) return s;
  return s.slice(4, 8) + s.slice(2, 4) + s.slice(0, 2);
}

function shiftDateYmd(yyyymmdd, deltaDays) {
  if (!yyyymmdd || String(yyyymmdd).length !== 8) return "";
  const y = Number(String(yyyymmdd).slice(0, 4));
  const m = Number(String(yyyymmdd).slice(4, 6)) - 1;
  const d = Number(String(yyyymmdd).slice(6, 8));
  const date = new Date(y, m, d);
  date.setDate(date.getDate() + deltaDays);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}${mm}${dd}`;
}

/** ------------------------------------------------------------------
 *  ChartCard: การ์ดมาตรฐานในธีม TO BE NUMBER ONE ESPORTS
 *  -----------------------------------------------------------------*/
function ChartCard({ title, icon, children, full = false, minHeight = 340, area, type = "us" }) {
  const isUs = type === "us";

  return (
    <motion.div
      style={{
        background: theme.bgCard,
        borderRadius: 14,
        padding: 20,
        boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: full ? "1 / -1" : "auto",
        gridArea: area || "auto",
        position: "relative",
        overflow: "hidden",
      }}
      whileHover={{ scale: 1.005, boxShadow: `0 0 22px ${isUs ? theme.primaryGlow : theme.enemyGlow}` }}
    >
      {/* Corner accents */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 60,
        height: 60,
        borderTop: `2px solid ${isUs ? theme.primary : theme.enemy}`,
        borderLeft: `2px solid ${isUs ? theme.primary : theme.enemy}`,
        borderRadius: "16px 0 0 0",
        pointerEvents: "none",
      }} />

      <div style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        width: 60,
        height: 60,
        borderBottom: `2px solid ${isUs ? theme.primary : theme.enemy}`,
        borderRight: `2px solid ${isUs ? theme.primary : theme.enemy}`,
        borderRadius: "0 0 16px 0",
        pointerEvents: "none",
      }} />

      {/* Header glow */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: `linear-gradient(90deg, transparent, ${isUs ? theme.primary : theme.enemy}, transparent)`,
        opacity: 0.35,
      }} />

      {title && (
        <div
          style={{
            color: theme.primaryLight,
            fontWeight: 900,
            fontSize: "1.05rem",
            letterSpacing: 0.4,
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 6,
            textShadow: "none",
          }}
        >
          {icon ? <span style={{ fontSize: 15, opacity: 0.85 }}>{icon}</span> : null}
          <span>{title}</span>
        </div>
      )}
      <div style={{ position: "relative", height: minHeight, zIndex: 2 }}>
        {children}
      </div>
    </motion.div>
  );
}

export default function HomePage({ games, comboStats, dropdownDates, sourceName }) {
  const navigate = useNavigate();

  // สไตล์ปุ่ม เริ่มดราฟ ในธีม TO BE NUMBER ONE ESPORTS
  const draftButtonStyle = {
    background: BUTTON_VARIANTS.primary.background,
    color: BUTTON_VARIANTS.primary.color,
    border: "none",
    borderRadius: 12,
    padding: "14px 32px",
    fontWeight: 900,
    fontSize: "1.1rem",
    boxShadow: BUTTON_VARIANTS.primary.boxShadow,
    cursor: "pointer",
    transition: "all 0.2s",
    marginBottom: 30,
    letterSpacing: "1px",
    textTransform: "uppercase",
    fontFamily: "'Orbitron', sans-serif",
    position: "relative",
    overflow: "hidden",
  };

  // Filter States
  const [filter, setFilter] = useState({
    result: "all",
    side: "all",
    competitor: "all",
  });
  const [search, setSearch] = useState("");
  const [competitors, setCompetitors] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [showSidebar, setShowSidebar] = useState(false);
  const [filterMode, setFilterMode] = useState("date");
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedWeekRange, setSelectedWeekRange] = useState({
    from: "",
    to: "",
  });
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState({
    from: "",
    to: "",
  });

  const rowsPerPage = 15;

  // Preprocess data
  const validGames = useMemo(
    () => games.filter((g) => g.Date && String(g.Date).length === 8),
    [games]
  );

  const weekLabels = useMemo(() => {
    const set = new Set(validGames.map(getWeekOfGame).filter(Boolean));
    return [...set].sort((a, b) => {
      const na = Number(String(a).replace(/^\D+/, "")) || 0;
      const nb = Number(String(b).replace(/^\D+/, "")) || 0;
      return na - nb || String(a).localeCompare(String(b));
    });
  }, [validGames]);

  const dateOptions = useMemo(() => {
    const source = dropdownDates?.length > 0
      ? dropdownDates
      : [...new Set(validGames.map((g) => g.Date).filter(Boolean))].sort();
    return source.map((d) => ({
      value: String(d).replace(".0", ""),
      label: formatDateDisplay(d)
    }));
  }, [dropdownDates, validGames]);

  const types = useMemo(
    () =>
      [
        ...new Set(
          validGames.map((g) => g.type).filter((x) => x && x !== "ไม่พบข้อมูล")
        ),
      ],
    [validGames]
  );

  const allCompetitors = useMemo(
    () => [...new Set(validGames.map((g) => g.competitor).filter(Boolean))],
    [validGames]
  );

  const weekOptions = weekLabels.map((w) => ({ value: w, label: w }));

  // Hero types
  const heroTypes = useMemo(() => {
    const counts = {};
    validGames.forEach((g) => {
      const t = g.type || g.Type || "ไม่พบข้อมูล";
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([t]) => t);
  }, [validGames]);

  // Filtered for table
  const filteredGames = useMemo(() => {
    let arr = validGames;
    if (filter.result !== "all") {
      arr = arr.filter(
        (g) =>
          (isFsWin(g)) ===
          (filter.result === "Win")
      );
    }
    if (filter.side !== "all") {
      arr = arr.filter(
        (g) => (g.side || g.Side || "").toLowerCase() === filter.side
      );
    }
    if (filter.competitor !== "all") {
      arr = arr.filter((g) => g.competitor === filter.competitor);
    }
    if (competitors.length > 0) {
      const sel = competitors.map((x) => x.value);
      arr = arr.filter((g) => sel.includes(g.competitor));
    }
    if (selectedTypes.length > 0) {
      const sel = selectedTypes.map((x) => x.value);
      arr = arr.filter((g) => sel.includes(g.type));
    }
    if (filterMode === "week") {
      if (selectedWeeks.length > 0) {
        arr = arr.filter((g) => selectedWeeks.includes(getWeekOfGame(g)));
      } else if (selectedWeekRange.from && selectedWeekRange.to) {
        const f = weekLabels.indexOf(selectedWeekRange.from);
        const t = weekLabels.indexOf(selectedWeekRange.to);
        if (f !== -1 && t !== -1) {
          const sl = weekLabels.slice(Math.min(f, t), Math.max(f, t) + 1);
          arr = arr.filter((g) => sl.includes(getWeekOfGame(g)));
        }
      }
    } else {
      if (selectedDates.length > 0) {
        arr = arr.filter((g) => selectedDates.includes(g.Date));
      }
      if (selectedDateRange.from) {
        arr = arr.filter((g) => g.Date >= selectedDateRange.from);
      }
      if (selectedDateRange.to) {
        arr = arr.filter((g) => g.Date <= selectedDateRange.to);
      }
    }
    if (search.trim()) {
      const txt = search.trim().toLowerCase();
      arr = arr.filter(
        (g) =>
          (g.competitor &&
            g.competitor.toLowerCase().includes(txt)) ||
          (g.type && g.type.toLowerCase().includes(txt)) ||
          (g.endType && g.endType.toLowerCase().includes(txt)) ||
          (g.Date && formatDateShort(g.Date).includes(txt))
      );
    }
    return [...arr].sort((a, b) =>
      String(b.Date).localeCompare(String(a.Date))
    );
  }, [
    validGames,
    filter,
    search,
    competitors,
    selectedTypes,
    filterMode,
    selectedWeeks,
    selectedWeekRange,
    selectedDates,
    selectedDateRange,
    weekLabels,
  ]);

  // Filtered for charts
  const filteredGamesForCharts = useMemo(() => {
    let arr = validGames;
    if (filter.result !== "all") {
      arr = arr.filter(
        (g) =>
          (isFsWin(g)) ===
          (filter.result === "Win")
      );
    }
    if (filter.side !== "all") {
      arr = arr.filter(
        (g) => (g.side || g.Side || "").toLowerCase() === filter.side
      );
    }
    if (filter.competitor !== "all") {
      arr = arr.filter((g) => g.competitor === filter.competitor);
    }
    if (competitors.length > 0) {
      const sel = competitors.map((x) => x.value);
      arr = arr.filter((g) => sel.includes(g.competitor));
    }
    if (selectedTypes.length > 0) {
      const sel = selectedTypes.map((x) => x.value);
      arr = arr.filter((g) => sel.includes(g.type));
    }
    if (filterMode === "week") {
      if (selectedWeeks.length > 0) {
        arr = arr.filter((g) => selectedWeeks.includes(getWeekOfGame(g)));
      } else if (selectedWeekRange.from && selectedWeekRange.to) {
        const f = weekLabels.indexOf(selectedWeekRange.from),
          t = weekLabels.indexOf(selectedWeekRange.to);
        if (f !== -1 && t !== -1) {
          const sl = weekLabels.slice(
            Math.min(f, t),
            Math.max(f, t) + 1
          );
          arr = arr.filter((g) => selectedWeeks.includes(getWeekOfGame(g)));
        }
      }
    } else {
      if (selectedDates.length > 0) {
        arr = arr.filter((g) => selectedDates.includes(g.Date));
      }
      if (selectedDateRange.from) {
        arr = arr.filter((g) => g.Date >= selectedDateRange.from);
      }
      if (selectedDateRange.to) {
        arr = arr.filter((g) => g.Date <= selectedDateRange.to);
      }
    }
    if (search.trim()) {
      const txt = search.trim().toLowerCase();
      arr = arr.filter(
        (g) =>
          (g.competitor &&
            g.competitor.toLowerCase().includes(txt)) ||
          (g.type && g.type.toLowerCase().includes(txt)) ||
          (g.endType && g.endType.toLowerCase().includes(txt)) ||
          (g.Date && formatDateShort(g.Date).includes(txt))
      );
    }
    return arr;
  }, [
    validGames,
    filter,
    search,
    competitors,
    selectedTypes,
    filterMode,
    selectedWeeks,
    selectedWeekRange,
    selectedDates,
    selectedDateRange,
    weekLabels,
  ]);

  const executiveKpis = useMemo(() => {
    const matches = filteredGamesForCharts.length;
    const wins = filteredGamesForCharts.filter(
      (g) => isFsWin(g)
    ).length;
    const winRate = matches > 0 ? `${((wins / matches) * 100).toFixed(1)}%` : "0%";

    const { dates: dateSorted, latest: latestDate, futureCount } = playedDates(filteredGamesForCharts);
    const range7 = latestDate ? dateSorted.filter((d) => d >= shiftDateYmd(latestDate, -6) && d <= latestDate) : [];
    const range7Set = new Set(range7);
    const games7 = filteredGamesForCharts.filter((g) => range7Set.has(String(g.Date || "").replace(".0", "")));
    const wins7 = games7.filter(
      (g) => isFsWin(g)
    ).length;
    const form7 = games7.length > 0 ? `${((wins7 / games7.length) * 100).toFixed(1)}%` : "-";

    const opponentCount = {};
    filteredGamesForCharts.forEach((g) => {
      const opp = String(g.competitor || "").trim();
      if (!opp) return;
      opponentCount[opp] = (opponentCount[opp] || 0) + 1;
    });
    const topOpponent = Object.entries(opponentCount).sort((a, b) => b[1] - a[1])[0];

    return {
      winRate,
      matches,
      form7,
      topOpponent: topOpponent ? `${topOpponent[0]} (${topOpponent[1]})` : "-",
      lastUpdated: formatDateTimeDisplay(latestDate),
      futureDated: futureCount,
    };
  }, [filteredGamesForCharts]);

  // --- CHARTS DATA & OPTIONS ---

  // 1. Pie Win/Loss
  const totalWin = filteredGamesForCharts.filter(
    (g) =>
      isFsWin(g)
  ).length;
  const totalLose = filteredGamesForCharts.length - totalWin;

  const pieWinLose = {
    labels: ["Loss", "Win"],
    datasets: [
      {
        data: [totalLose, totalWin],
        backgroundColor: [theme.danger, theme.success],
        borderWidth: 2,
        borderColor: theme.bgCard,
        hoverBorderWidth: 3,
        hoverOffset: 15,
      },
    ],
  };

  const pieWinLoseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: -Math.PI,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        maxHeight: 42,
        labels: {
          font: { size: 12, weight: "bold" },
          color: theme.textDim,
          usePointStyle: true,
          padding: 10,
          boxWidth: 10,
          boxHeight: 10,
          pointStyle: "circle",
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: theme.bgCard,
        titleColor: theme.primary,
        bodyColor: theme.textDim,
        borderColor: theme.border,
        borderWidth: 1,
        titleFont: { size: 16, weight: "bold" },
        bodyFont: { size: 16 },
        callbacks: {
          label: (ctx) => {
            const v = ctx.raw || 0;
            const tot = ctx.dataset.data.reduce((a, b) => a + b, 0);
            return `${ctx.label}: ${v} (${Math.round((v / tot) * 100)}%)`;
          },
        },
      },
      datalabels: {
        display: true,
        color: "#fff",
        font: { size: 12, weight: "bold" },
        textStrokeColor: "rgba(0,0,0,0.35)",
        textStrokeWidth: 2,
        formatter: (v, ctx) => {
          const tot = ctx.dataset.data.reduce((a, b) => a + b, 0);
          if (!tot || v <= 0) return "";
          return `${Math.round((v / tot) * 100)}%`;
        },
      },
    },
    cutout: "76%",
    layout: { padding: 8 },
    animation: { animateScale: true, animateRotate: true },
  };

  // 2. Hero Performance by Week (Radar)
  const dynamicWeekLabels = useMemo(() => {
    const set = new Set(filteredGamesForCharts.map(getWeekOfGame).filter(Boolean));
    return [...set].sort((a, b) => {
      const na = Number(String(a).replace(/^\D+/, "")) || 0;
      const nb = Number(String(b).replace(/^\D+/, "")) || 0;
      return na - nb || String(a).localeCompare(String(b));
    });
  }, [filteredGamesForCharts]);

  const heroRadarData = useMemo(() => {
    const topHeroes = heroTypes.slice(0, 5);
    return {
      labels: dynamicWeekLabels,
      datasets: topHeroes.map((hero, idx) => {
        const data = dynamicWeekLabels.map((w) => {
          const gamesIn = filteredGamesForCharts.filter(
            (g) => getWeekOfGame(g) === w && g.type === hero
          );
          const wins = gamesIn.filter(
            (g) =>
              isFsWin(g)
          ).length;
          return gamesIn.length
            ? Number(((wins / gamesIn.length) * 100).toFixed(1))
            : 0;
        });
        const color = heroColors[idx % heroColors.length];
        return {
          label: hero,
          data,
          borderColor: color,
          backgroundColor: hexToRgba(color, 0.2),
          pointBackgroundColor: color,
          pointBorderColor: theme.bgCard,
          pointRadius: 5,
          pointHoverRadius: 8,
          fill: true,
          tension: 0.3,
        };
      }),
    };
  }, [dynamicWeekLabels, heroTypes, filteredGamesForCharts]);

  const heroRadarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: { color: theme.border, lineWidth: 2 },
        grid: { color: "rgba(255,16,38,0.1)" },
        suggestedMin: 0,
        suggestedMax: 100,
        pointLabels: {
          color: theme.primary,
          font: { size: 12, weight: "bold" },
          padding: 8,
        },
        ticks: {
          color: theme.primaryLight,
          font: { size: 11, weight: "bold" },
          showLabelBackdrop: false,
          stepSize: 20,
          callback: (v) => `${v}%`,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        maxHeight: 70,
        labels: {
          font: { size: 12, weight: "bold" },
          color: theme.primary,
          usePointStyle: true,
          padding: 12,
          boxWidth: 10,
          boxHeight: 10,
          pointStyle: "rectRounded",
        },
      },
      tooltip: {
        backgroundColor: theme.bgCard,
        titleColor: theme.primary,
        bodyColor: theme.text,
        bodyFont: { size: 17, weight: "bold" },
        borderColor: theme.border,
        borderWidth: 1.5,
        callbacks: {
          title: (tooltipItems) => tooltipItems[0].dataset.label,
          label: (tooltipItem) => `WinRate: ${tooltipItem.raw}%`,
        },
      },
      datalabels: { display: false },
    },
    layout: { padding: 8 },
  };

  // 3. End Type Distribution (Pie)
  const endTypeMap = {};
  filteredGames.forEach((g) => {
    const k = g.endType || g.EndType || "ไม่พบข้อมูล";
    endTypeMap[k] = (endTypeMap[k] || 0) + 1;
  });
  const endTypeLabels = Object.keys(endTypeMap);

  const endTypePie = {
    labels: endTypeLabels,
    datasets: [
      {
        data: Object.values(endTypeMap),
        backgroundColor: [
          "#ff7a1a",
          "#11e997",
          "#ff3b7a",
          "#ffb020",
          "#ff5c4d",
          "#f28b30",
          "#33d9ff",
          "#b27bff",
        ].slice(0, endTypeLabels.length),
        borderWidth: 2,
        borderColor: theme.bgCard,
        hoverBorderWidth: 3,
        hoverOffset: 15,
      },
    ],
  };

  const endTypePieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        maxHeight: 70,
        labels: {
          font: { weight: "bold", size: 12 },
          padding: 8,
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
          pointStyle: "circle",
          color: theme.textDim,
        },
      },
      tooltip: {
        backgroundColor: theme.bgCard,
        titleColor: theme.primary,
        bodyColor: theme.textDim,
        borderColor: theme.border,
        bodyFont: { size: 16 },
        callbacks: {
          label: (ctx) => {
            const v = ctx.raw || 0;
            const tot = ctx.dataset.data.reduce((a, b) => a + b, 0);
            return `${ctx.label}: ${v} (${Math.round((v / tot) * 100)}%)`;
          },
        },
      },
      datalabels: {
        display: true,
        color: theme.text,
        font: { size: 12, weight: "bold" },
        formatter: (v, ctx) => {
          const tot = ctx.dataset.data.reduce((a, b) => a + b, 0);
          if (!tot || v <= 0) return "";
          return `${Math.round((v / tot) * 100)}%`;
        },
      },
    },
    cutout: "72%",
    layout: { padding: 8 },
    animation: { animateScale: true, animateRotate: true },
  };

  // 4. Win/Loss by Rival (Bar)
  const rivalsSet = new Set(
    filteredGamesForCharts.map((g) => g.competitor).filter(Boolean)
  );

  const rivals = [...rivalsSet].sort((a, b) => {
    const ia = PRIORITY_TEAMS.indexOf(a);
    const ib = PRIORITY_TEAMS.indexOf(b);
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    }
    const ta = TEAM_TIER_MAP[a] ?? 99;
    const tb = TEAM_TIER_MAP[b] ?? 99;
    if (ta !== tb) return ta - tb;
    return String(a).localeCompare(String(b));
  });

  const winByRival = useMemo(() => {
    const m = {};
    rivals.forEach((r) => {
      m[r] = filteredGamesForCharts.filter(
        (g) =>
          g.competitor === r &&
          isFsWin(g)
      ).length;
    });
    return m;
  }, [rivals, filteredGamesForCharts]);

  const loseByRival = useMemo(() => {
    const m = {};
    rivals.forEach((r) => {
      m[r] = filteredGamesForCharts.filter(
        (g) =>
          g.competitor === r &&
          String(g.match || g["Match Win"] || "").toUpperCase() !== "FS"
      ).length;
    });
    return m;
  }, [rivals, filteredGamesForCharts]);

  const rivalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        maxHeight: 54,
        labels: {
          font: { size: 12, weight: "bold" },
          color: theme.primary,
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 10,
          boxHeight: 10,
          padding: 10,
        },
      },
      tooltip: {
        backgroundColor: theme.bgCard,
        titleColor: theme.primary,
        bodyColor: theme.text,
        borderColor: theme.border,
        borderWidth: 1.5,
        bodyFont: { size: 16 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(255,16,38,0.1)",
          borderDash: [4, 6]
        },
        ticks: {
          color: theme.textDim,
          font: { size: 12, weight: "bold" },
        },
      },
      x: {
        grid: { display: false },
        ticks: {
          color: theme.textDim,
          font: { size: 11, weight: "bold" },
        },
      },
    },
  };

  // 5. Dual Pie for Game Duration
  const shortGames = filteredGames.filter(
    (g) => !isNaN(+g.time) && Number(g.time) < 15
  );
  const longGames = filteredGames.filter(
    (g) => !isNaN(+g.time) && Number(g.time) >= 15
  );

  const shortWinList = shortGames
    .filter(
      (g) =>
        isFsWin(g)
    )
    .map((g) => `${getWeekOfGame(g) || "-"} (${formatTime(g.time)} นาที)`);

  const shortLoseList = shortGames
    .filter(
      (g) =>
        String(g.match || g["Match Win"] || "").toUpperCase() !== "FS"
    )
    .map((g) => `${getWeekOfGame(g) || "-"} (${formatTime(g.time)} นาที)`);

  const longWinList = longGames
    .filter(
      (g) =>
        isFsWin(g)
    )
    .map((g) => `${getWeekOfGame(g) || "-"} (${formatTime(g.time)} นาที)`);

  const longLoseList = longGames
    .filter(
      (g) =>
        String(g.match || g["Match Win"] || "").toUpperCase() !== "FS"
    )
    .map((g) => `${getWeekOfGame(g) || "-"} (${formatTime(g.time)} นาที)`);

  const pieShortData = {
    labels: ["Loss", "Win"],
    datasets: [
      {
        data: [shortLoseList.length, shortWinList.length],
        backgroundColor: [theme.danger, theme.success],
        borderWidth: 2,
        borderColor: theme.bgCard,
      },
    ],
  };

  const pieLongData = {
    labels: ["Loss", "Win"],
    datasets: [
      {
        data: [longLoseList.length, longWinList.length],
        backgroundColor: [theme.danger, theme.success],
        borderWidth: 2,
        borderColor: theme.bgCard,
      },
    ],
  };

  const pieSimpleOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme.bgCard,
        titleColor: theme.primary,
        bodyColor: theme.textDim,
        borderColor: theme.border,
        mode: "nearest",
        intersect: false,
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.raw} เกม`,
          afterBody: (ctxArr) => {
            const idx = ctxArr[0].dataIndex;
            const isShort = ctxArr[0].dataset === pieShortData.datasets[0];
            const list = isShort
              ? (idx === 1 ? shortWinList : shortLoseList)
              : (idx === 1 ? longWinList : longLoseList);
            return ["สัปดาห์/เวลา:", ...list.map((it) => `• ${it}`)];
          },
        },
      },
    },
    layout: { padding: 8 },
  };

  // Reset pagination on filter changes
  React.useEffect(() => setPage(1), [
    search,
    filter,
    competitors,
    selectedTypes,
    filterMode,
    selectedWeeks,
    selectedWeekRange,
    selectedDates,
    selectedDateRange,
    validGames.length,
  ]);

  // Styles for table & inputs
  const chartTitleStyle = {
    color: theme.primary,
    fontWeight: 900,
    fontSize: "1.18rem",
    marginBottom: 12,
    letterSpacing: 0.6,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    textShadow: `0 0 15px ${theme.primaryGlow}`,
  };

  const thStyle = {
    background: theme.tableHeader,
    color: theme.primary,
    padding: "12px 8px",
    fontWeight: 800,
    textAlign: "center",
    fontSize: "1.08rem",
    borderBottom: theme.tableHeaderBorder,
    position: "sticky",
    top: 0,
    zIndex: 1,
    textShadow: `0 0 10px ${theme.primaryGlow}`,
  };

  const tdStyle = {
    padding: "12px 8px",
    borderBottom: "1px solid rgba(255,16,38,0.1)",
    textAlign: "center",
    fontWeight: 700,
    transition: "all 0.2s",
    color: theme.textDim,
  };

  const inputStyle = {
    background: theme.bgCard,
    border: `1.5px solid ${theme.border}`,
    borderRadius: 12,
    padding: "8px 12px 8px 36px",
    fontWeight: 700,
    fontSize: "1.01rem",
    color: theme.text,
    outline: "none",
    minWidth: 100,
    boxShadow: `0 1.5px 7px ${theme.primaryGlow}55`,
    transition: "all 0.2s",
  };

  const clearBtnStyle = {
    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    color: "#fff",
    fontWeight: 800,
    border: "none",
    borderRadius: 12,
    padding: "8px 18px",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "all 0.2s",
    boxShadow: `0 4px 20px ${theme.primaryGlow}`,
  };

  const reactSelectStyle = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 13,
      minHeight: 38,
      border: state.isFocused
        ? `2.2px solid ${theme.primary}`
        : `1.4px solid ${theme.border}`,
      boxShadow: state.isFocused
        ? `0 0 0 2px ${theme.primaryGlow}33`
        : `0 2px 12px ${theme.primaryGlow}22`,
      fontWeight: 700,
      fontSize: "1rem",
      color: theme.primary,
      background: theme.bgCard,
      outline: "none",
    }),
    multiValue: (provided) => ({
      ...provided,
      background: theme.primarySoft,
      color: theme.primary,
      fontWeight: 700,
      borderRadius: 8,
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isSelected
        ? theme.primary
        : state.isFocused
          ? theme.primarySoft
          : theme.bgCard,
      color: state.isSelected ? theme.bg : theme.text,
      fontWeight: 700,
    }),
    menu: (provided) => ({
      ...provided,
      background: theme.bgCard,
      border: `1px solid ${theme.border}`,
      boxShadow: `0 10px 40px ${theme.primaryGlow}`,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.text,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme.textMuted,
    }),
  };

  const selectTheme = (t) => ({
    ...t,
    colors: {
      ...t.colors,
      primary: theme.primary,
      primary25: theme.primarySoft,
      neutral0: theme.bgCard,
      neutral80: theme.text,
    },
  });

  // CSS animations for keyframes
  const styleTag = `
    @keyframes scan { 
      0% { top: -10%; } 
      100% { top: 110%; } 
    }
    @keyframes fadeInUp { 
      from { opacity: 0; transform: translateY(30px); } 
      to { opacity: 1; transform: translateY(0); } 
    }
    @keyframes pulse-glow { 
      0%, 100% { opacity: 0.6; } 
      50% { opacity: 1; } 
    }
    @keyframes pulse-fast { 
      0%, 100% { opacity: 0.7; } 
      50% { opacity: 1; } 
    }
    @keyframes radar-sweep { 
      0% { transform: rotate(0deg); } 
      100% { transform: rotate(360deg); } 
    }
    @keyframes data-flow { 
      0% { background-position: 0% 50%; } 
      100% { background-position: 200% 50%; } 
    }
    @keyframes glow-pulse { 
      0%, 100% { box-shadow: 0 0 20px ${theme.primaryGlow}; } 
      50% { box-shadow: 0 0 40px ${theme.primaryGlow}, 0 0 60px ${theme.primaryGlow}; } 
    }
  `;

  return (
    <>
      <style>{styleTag}</style>
      <div
        style={{
          minHeight: "100vh",
          background: theme.bgGradient,
          fontFamily: "'Kanit', 'Rajdhani', sans-serif",
          paddingBottom: 90,
          position: "relative",
          color: theme.text,
        }}
      >
        {/* Background effects */}
        <div style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.35,
          background: `
            radial-gradient(ellipse at 30% 0%, ${theme.primaryGlow} 0%, transparent 50%),
            radial-gradient(ellipse at 70% 100%, rgba(255,16,38,0.2) 0%, transparent 50%),
            linear-gradient(180deg, rgba(255,16,38,0.03) 0%, transparent 30%)
          `,
          zIndex: 0,
        }} />

        <div style={{
          position: "fixed",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,16,38,0.016) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,16,38,0.016) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          pointerEvents: "none",
          zIndex: 0,
        }} />

        {/* ปุ่มเมนูหลัก */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 14,
            position: "relative",
            zIndex: 2,
            paddingTop: 30,
            flexWrap: "wrap",
          }}
        >
          <motion.button
            style={draftButtonStyle}
            whileHover={{ scale: 1.05, boxShadow: `0 12px 40px ${theme.primaryGlow}` }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/draft")}
          >
            <span style={{ position: "relative", zIndex: 2 }}>เริ่มดราฟ</span>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                animation: "data-flow 2s linear infinite",
                zIndex: 1,
              }}
            />
          </motion.button>

          <motion.button
            style={{
              ...draftButtonStyle,
              background: BUTTON_VARIANTS.primarySoft.background,
              boxShadow: BUTTON_VARIANTS.primarySoft.boxShadow,
            }}
            whileHover={{ scale: 1.05, boxShadow: "0 12px 40px rgba(255,16,38,0.45)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/meta-lab")}
          >
            <span style={{ position: "relative", zIndex: 2 }}>Meta Lab</span>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                animation: "data-flow 2s linear infinite",
                zIndex: 1,
              }}
            />
          </motion.button>

          <motion.button
            style={{
              ...draftButtonStyle,
              background: BUTTON_VARIANTS.primarySoft.background,
              boxShadow: BUTTON_VARIANTS.primarySoft.boxShadow,
            }}
            whileHover={{ scale: 1.05, boxShadow: "0 12px 40px rgba(255,16,38,0.45)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/tier-data")}
          >
            <span style={{ position: "relative", zIndex: 2 }}>TierData</span>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                animation: "data-flow 2s linear infinite",
                zIndex: 1,
              }}
            />
          </motion.button>

          <motion.button
            style={{
              ...draftButtonStyle,
              background: "linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%)",
              boxShadow: "0 8px 30px rgba(162, 155, 254, 0.35)",
            }}
            whileHover={{ scale: 1.05, boxShadow: "0 12px 40px rgba(162, 155, 254, 0.45)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/tier-data")}
          >
            <span style={{ position: "relative", zIndex: 2 }}>TierData</span>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                animation: "data-flow 2s linear infinite",
                zIndex: 1,
              }}
            />
          </motion.button>

          <motion.button
            style={{
              ...draftButtonStyle,
              background: "linear-gradient(135deg, #ff2d55 0%, #ff6b6b 100%)",
              boxShadow: "0 8px 30px rgba(255, 107, 107, 0.35)",
            }}
            whileHover={{ scale: 1.05, boxShadow: "0 12px 40px rgba(255, 107, 107, 0.45)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/draft-recap")}
          >
            <span style={{ position: "relative", zIndex: 2 }}>Draft Recap</span>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                animation: "data-flow 2s linear infinite",
                zIndex: 1,
              }}
            />
          </motion.button>

          <motion.button
            style={{
              ...draftButtonStyle,
              background: "linear-gradient(135deg, #0f9b8e 0%, #45c4b0 100%)",
              boxShadow: "0 8px 30px rgba(69, 196, 176, 0.35)",
            }}
            whileHover={{ scale: 1.05, boxShadow: "0 12px 40px rgba(69, 196, 176, 0.45)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/tactic-board")}
          >
            <span style={{ position: "relative", zIndex: 2 }}>Sucrose Tactic</span>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                animation: "data-flow 2s linear infinite",
                zIndex: 1,
              }}
            />
          </motion.button>

        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            padding: "30px 20px 10px",
            textAlign: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <h1 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: "2.8rem",
            fontWeight: 900,
            color: theme.primary,
            textTransform: "uppercase",
            letterSpacing: "6px",
            marginBottom: "8px",
            textShadow: `0 0 16px ${theme.primaryGlow}`,
          }}>
            TO BE NUMBER ONE ESPORTS
          </h1>
          <div style={{
            fontFamily: "'Kanit', sans-serif",
            fontSize: "1rem",
            fontWeight: 700,
            color: theme.textDim,
            letterSpacing: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
          }}>
            <span style={{
              width: "40px",
              height: "2px",
              background: `linear-gradient(90deg, transparent, ${theme.primary}, transparent)`,
            }} />
            TO BE NUMBER ONE ESPORTS • DRAFT SYSTEM
            <span style={{
              width: "40px",
              height: "2px",
              background: `linear-gradient(90deg, transparent, ${theme.primary}, transparent)`,
            }} />
          </div>
          <p style={{ margin: "10px 0 0", color: theme.secondary, fontWeight: 700, fontSize: 13 }}>
            stat จาก {sourceName ? String(sourceName).replace(/\.(xlsx|xls)$/i, "") : "ไฟล์ที่อัปโหลด"} เท่านั้น
          </p>
          <p style={{ margin: "8px 0 0", color: theme.textDim, fontWeight: 700, fontSize: 12 }}>
            Last updated: {executiveKpis.lastUpdated}
            {executiveKpis.futureDated > 0 && (
              <span style={{ color: theme.warning || "#f59e0b", marginLeft: 8 }}>
                ⚠ มี {executiveKpis.futureDated} เกมลงวันที่ล่วงหน้าในชีท (ไม่นับเป็นวันล่าสุด)
              </span>
            )}
          </p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ staggerChildren: 0.1 }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
            margin: "28px auto 8px auto",
            maxWidth: 1100,
            padding: "0 20px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <SummaryCard
            icon={icons.trophy}
            label="Win Rate"
            value={executiveKpis.winRate}
            color={theme.success}
            gradient={`linear-gradient(135deg, ${theme.success} 0%, #00cc6a 100%)`}
          />
          <SummaryCard
            icon={icons.chart}
            label="Total Matches"
            value={executiveKpis.matches}
            color={theme.primary}
            gradient={`linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`}
          />
          <SummaryCard
            icon={icons.fire}
            label="Last 7 Days Form"
            value={executiveKpis.form7}
            color={theme.secondary}
            gradient={`linear-gradient(135deg, ${theme.secondary} 0%, #ff8c00 100%)`}
          />
          <SummaryCard
            icon={icons.sword}
            label="Top Opponent"
            value={executiveKpis.topOpponent}
            color={theme.enemy}
            gradient={`linear-gradient(135deg, ${theme.enemy} 0%, #cc0000 100%)`}
          />
        </motion.div>

        {/* Sidebar Toggle */}
        <div
          style={{
            position: "fixed",
            top: 30,
            right: 30,
            zIndex: 120,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {!showSidebar && (
            <motion.button
              onClick={() => setShowSidebar(true)}
              whileHover={{ scale: 1.1 }}
              style={{
                background: theme.bgCard,
                border: `2.5px solid ${theme.primary}`,
                color: theme.primary,
                borderRadius: 17,
                fontWeight: 800,
                fontSize: 23,
                boxShadow: `0 4px 24px ${theme.primaryGlow}bb`,
                cursor: "pointer",
                padding: "6px 15px",
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontFamily: "'Kanit', sans-serif",
              }}
              title="เปิดฟิลเตอร์"
            >
              {icons.menu} ฟิลเตอร์
            </motion.button>
          )}
        </div>

        <AnimatePresence>
          {showSidebar && (
            <motion.div
              key="sidebar"
              initial={{ x: 340, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 360, opacity: 0 }}
              transition={{ type: "spring", stiffness: 110, damping: 16 }}
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                height: "100vh",
                width: 350,
                background: theme.bgCard,
                borderLeft: `2px solid ${theme.border}`,
                boxShadow: `0 0 38px 0 ${theme.primaryGlow}85`,
                zIndex: 200,
                display: "flex",
                flexDirection: "column",
                padding: "0px",
                overflowY: "auto",
              }}
            >
              {/* Header + Close */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "linear-gradient(135deg, rgba(255,16,38,0.1), transparent)",
                  padding: "18px 22px 10px 18px",
                  borderBottom: `1.5px solid ${theme.border}`,
                }}
              >
                <div style={{
                  fontWeight: 900,
                  color: theme.primary,
                  fontSize: 22,
                  fontFamily: "'Orbitron', sans-serif",
                }}>
                  ฟิลเตอร์ข้อมูล
                </div>
                <motion.button
                  onClick={() => setShowSidebar(false)}
                  whileHover={{ scale: 1.2, rotate: 90 }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: theme.enemy,
                    fontWeight: 900,
                    fontSize: "1.8rem",
                    cursor: "pointer",
                    marginLeft: 5,
                    marginRight: -8,
                  }}
                  title="ย่อกลับ"
                >
                  ⏩
                </motion.button>
              </div>

              {/* Clear All */}
              <div
                style={{
                  padding: "12px 18px 7px 18px",
                  borderBottom: `1.5px solid ${theme.border}`,
                  background: theme.bgCard,
                }}
              >
                <button
                  style={{
                    ...clearBtnStyle,
                    background: "linear-gradient(135deg, rgba(255,16,38,0.1), rgba(255,16,38,0.05))",
                    color: theme.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    width: "100%",
                    fontSize: "1.1rem",
                    padding: "11px 0",
                    marginBottom: 2,
                    border: `1px solid ${theme.border}`,
                  }}
                  onClick={() => {
                    setFilter({ result: "all", side: "all", competitor: "all" });
                    setSearch("");
                    setCompetitors([]);
                    setSelectedTypes([]);
                    setFilterMode("week");
                    setSelectedWeeks([]);
                    setSelectedWeekRange({ from: "", to: "" });
                    setSelectedDates([]);
                    setSelectedDateRange({ from: "", to: "" });
                  }}
                >
                  <span>ล้างกรองทั้งหมด</span>
                  <span style={{ fontSize: "1.25rem" }}>🔄</span>
                </button>
              </div>

              {/* Week/Date Filter */}
              <div
                style={{
                  padding: "16px 18px 6px 18px",
                  background: theme.bgCard,
                  borderBottom: `1.5px solid ${theme.border}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 8,
                    fontWeight: 700,
                    color: theme.primary,
                  }}
                >
                  <span>{icons.calendar}</span>
                  <span>เลือกโหมดฟิลเตอร์:</span>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <button
                    style={{
                      flex: 1,
                      background: filterMode === "week"
                        ? `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`
                        : "rgba(255,16,38,0.1)",
                      color: filterMode === "week" ? "#fff" : theme.primary,
                      fontWeight: 800,
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 14px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontFamily: "'Kanit', sans-serif",
                    }}
                    onClick={() => setFilterMode("week")}
                  >
                    สัปดาห์
                  </button>
                  <button
                    style={{
                      flex: 1,
                      background: filterMode === "date"
                        ? `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`
                        : "rgba(255,16,38,0.1)",
                      color: filterMode === "date" ? "#fff" : theme.primary,
                      fontWeight: 800,
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 14px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontFamily: "'Kanit', sans-serif",
                    }}
                    onClick={() => setFilterMode("date")}
                  >
                    วันที่
                  </button>
                </div>

                {filterMode === "week" && (
                  <>
                    <div style={{ fontWeight: 700, color: theme.primary, margin: "8px 0 4px" }}>
                      เลือกสัปดาห์ (หลายรายการ)
                    </div>
                    <ReactSelect
                      isMulti
                      value={weekOptions.filter((w) => selectedWeeks.includes(w.value))}
                      onChange={(opts) => {
                        setSelectedWeeks(opts ? opts.map((w) => w.value) : []);
                        setSelectedWeekRange({ from: "", to: "" });
                      }}
                      options={weekOptions}
                      placeholder="เลือกสัปดาห์"
                      styles={reactSelectStyle}
                      theme={selectTheme}
                      isClearable
                    />
                    <div style={{ fontWeight: 700, color: theme.primary, margin: "10px 0 4px" }}>
                      หรือช่วงสัปดาห์
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <select
                        style={{ ...inputStyle, minWidth: 100, fontWeight: 700 }}
                        value={selectedWeekRange.from}
                        onChange={(e) => {
                          setSelectedWeekRange((r) => ({ ...r, from: e.target.value }));
                          setSelectedWeeks([]);
                        }}
                      >
                        <option value="">จาก</option>
                        {weekOptions.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      <select
                        style={{ ...inputStyle, minWidth: 100, fontWeight: 700 }}
                        value={selectedWeekRange.to}
                        onChange={(e) => {
                          setSelectedWeekRange((r) => ({ ...r, to: e.target.value }));
                          setSelectedWeeks([]);
                        }}
                      >
                        <option value="">ถึง</option>
                        {weekOptions.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {filterMode === "date" && (
                  <>
                    <div style={{ fontWeight: 700, color: theme.primary, margin: "8px 0 4px" }}>
                      เลือกวันที่ (หลายรายการ)
                    </div>
                    <ReactSelect
                      isMulti
                      value={dateOptions.filter((d) => selectedDates.includes(d.value))}
                      onChange={(opts) => {
                        setSelectedDates(opts ? opts.map((d) => d.value) : []);
                        setSelectedDateRange({ from: "", to: "" });
                      }}
                      options={dateOptions}
                      placeholder="เลือกวันที่"
                      styles={reactSelectStyle}
                      theme={selectTheme}
                      isClearable
                    />
                    <div style={{ fontWeight: 700, color: theme.primary, margin: "10px 0 4px" }}>
                      หรือช่วงวันที่
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <select
                        style={{ ...inputStyle, minWidth: 100, fontWeight: 700 }}
                        value={selectedDateRange.from}
                        onChange={(e) => {
                          setSelectedDateRange((r) => ({ ...r, from: e.target.value }));
                          setSelectedDates([]);
                        }}
                      >
                        <option value="">จาก</option>
                        {dateOptions.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      <select
                        style={{ ...inputStyle, minWidth: 100, fontWeight: 700 }}
                        value={selectedDateRange.to}
                        onChange={(e) => {
                          setSelectedDateRange((r) => ({ ...r, to: e.target.value }));
                          setSelectedDates([]);
                        }}
                      >
                        <option value="">ถึง</option>
                        {dateOptions.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>

              {/* Main Filters */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  padding: "18px 18px 8px 18px",
                  background: theme.bgCard,
                }}
              >
                {/* Match Result */}
                <div>
                  <div style={{ fontWeight: 700, color: theme.primary, marginBottom: 6, fontSize: 15 }}>
                    ผลการแข่งขัน
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <FilterTag
                      label="All"
                      active={filter.result === "all"}
                      onClick={() => setFilter((f) => ({ ...f, result: "all" }))}
                      color="rgba(255,16,38,0.2)"
                      icon={icons.filter}
                    />
                    <FilterTag
                      label="Win"
                      icon={icons.win}
                      active={filter.result === "Win"}
                      onClick={() => setFilter((f) => ({ ...f, result: f.result === "Win" ? "all" : "Win" }))}
                      color={theme.success}
                    />
                    <FilterTag
                      label="Loss"
                      icon={icons.lose}
                      active={filter.result === "Lose"}
                      onClick={() => setFilter((f) => ({ ...f, result: f.result === "Lose" ? "all" : "Lose" }))}
                      color={theme.danger}
                    />
                  </div>
                </div>
                <hr style={{ margin: "8px 0", border: "none", borderTop: "1px dashed rgba(255,16,38,0.3)" }} />

                {/* Side Filter */}
                <div>
                  <div style={{ fontWeight: 700, color: theme.primary, marginBottom: 6, fontSize: 15 }}>
                    ฝั่งที่เล่น
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <FilterTag
                      label="Red Side"
                      icon={icons.red}
                      active={filter.side === "red"}
                      onClick={() => setFilter((f) => ({ ...f, side: f.side === "red" ? "all" : "red" }))}
                      color={theme.enemy}
                    />
                    <FilterTag
                      label="Blue Side"
                      icon={icons.blue}
                      active={filter.side === "blue"}
                      onClick={() => setFilter((f) => ({ ...f, side: f.side === "blue" ? "all" : "blue" }))}
                      color={theme.blue}
                    />
                  </div>
                </div>
                <hr style={{ margin: "8px 0", border: "none", borderTop: "1px dashed rgba(255,16,38,0.3)" }} />

                {/* Multi-select competitor */}
                <div>
                  <div style={{ fontWeight: 700, color: theme.primary, marginBottom: 6, fontSize: 15 }}>
                    เลือกคู่แข่ง (เลือกหลาย)
                  </div>
                  <ReactSelect
                    isMulti
                    value={competitors}
                    onChange={setCompetitors}
                    options={allCompetitors.map((c) => ({ value: c, label: c }))}
                    placeholder="เลือกหลายคู่แข่ง"
                    styles={reactSelectStyle}
                    theme={selectTheme}
                    isClearable
                  />
                </div>
                <hr style={{ margin: "8px 0", border: "none", borderTop: "1px dashed rgba(255,16,38,0.3)" }} />

                {/* Multi-select Type */}
                <div>
                  <div style={{ fontWeight: 700, color: theme.primary, marginBottom: 6, fontSize: 15 }}>
                    เลือก Type (เลือกหลาย)
                  </div>
                  <ReactSelect
                    isMulti
                    value={selectedTypes}
                    onChange={setSelectedTypes}
                    options={types.map((t) => ({ value: t, label: t }))}
                    placeholder="เลือกหลาย Type"
                    styles={reactSelectStyle}
                    theme={selectTheme}
                    isClearable
                  />
                </div>
                <hr style={{ margin: "8px 0", border: "none", borderTop: "1px dashed rgba(255,16,38,0.3)" }} />

                {/* Single-select competitor */}
                <div>
                  <div style={{ fontWeight: 700, color: theme.primary, marginBottom: 6, fontSize: 15 }}>
                    เลือกคู่แข่ง (เลือกเดียว)
                  </div>
                  <select
                    value={filter.competitor}
                    onChange={(e) => setFilter((f) => ({ ...f, competitor: e.target.value }))}
                    style={{
                      background: theme.bgCard,
                      border: `1.5px solid ${theme.border}`,
                      borderRadius: 12,
                      padding: "8px 16px",
                      fontWeight: 700,
                      fontSize: "1.01rem",
                      color: theme.text,
                      outline: "none",
                      minWidth: 110,
                      boxShadow: `0 1.5px 7px ${theme.primaryGlow}55`,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      marginBottom: 2,
                    }}
                  >
                    <option value="all">ทุกคู่แข่ง</option>
                    {allCompetitors.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Search */}
                <div>
                  <div style={{ fontWeight: 700, color: theme.primary, marginBottom: 5, fontSize: 15 }}>
                    ค้นหาข้อมูล
                  </div>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder="ค้นหาคู่แข่งหรือคำอื่นๆ"
                      style={{
                        ...inputStyle,
                        paddingLeft: 36,
                        minWidth: 180,
                      }}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <span
                      style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "1.2rem",
                        opacity: 0.6,
                        color: theme.primary,
                      }}
                    >
                      {icons.search}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1 }} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Charts Section */}
        <section
          style={{
            maxWidth: "min(1600px, 98vw)",
            margin: "10px auto 0 auto",
            padding: "0 20px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              display: "grid",
              gap: 18,
              alignItems: "stretch",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
            }}
          >
            {/* Win/Loss Pie */}
            <ChartCard title="Win/Loss Ratio" minHeight={340} type="us">
              <div style={{ position: "relative", height: "88%", padding: "8px 10px 0 10px" }}>
                <Pie data={pieWinLose} options={{ ...pieWinLoseOptions, rotation: Math.PI / 2 }} />
              </div>
            </ChartCard>

            {/* End Type Pie */}
            <ChartCard title="End Type Mix" minHeight={340} type="us">
              <div style={{ position: "relative", height: "100%", padding: "8px 10px 0 10px" }}>
                <Pie data={endTypePie} options={endTypePieOptions} />
              </div>
            </ChartCard>

            <ChartCard title="Match Duration Split" minHeight={410} type="us">
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  height: "100%",
                  padding: "4px 8px 2px 8px",
                  alignContent: "flex-start",
                }}
              >
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <div style={{
                    textAlign: "center",
                    fontWeight: 800,
                    marginBottom: 4,
                    color: theme.primary,
                    textShadow: `0 0 10px ${theme.primaryGlow}`,
                  }}>
                    Under 15 min
                  </div>
                  <div style={{ position: "relative", height: 140, maxWidth: 280, margin: "0 auto" }}>
                    <Pie data={pieShortData} options={{
                      ...pieSimpleOptions,
                      rotation: 0,
                      circumference: 360,
                      cutout: "74%",
                      plugins: {
                        ...pieSimpleOptions.plugins,
                        datalabels: {
                          display: true,
                          color: "#fff",
                          font: { size: 12, weight: "bold" },
                          formatter: (value, ctx) => {
                            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                            if (total === 0) return "0%";
                            return `${Math.round((value / total) * 100)}%`;
                          }
                        }
                      }
                    }} />

                  </div>
                  {/* แสดงตัวเลขด้านล่างกราฟ */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginTop: 8,
                    fontSize: "11px",
                    color: theme.textDim
                  }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: theme.success, fontWeight: "bold", fontSize: "14px" }}>
                        {shortWinList.length}
                      </div>
                      <div>Win</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: theme.danger, fontWeight: "bold", fontSize: "14px" }}>
                        {shortLoseList.length}
                      </div>
                      <div>Loss</div>
                    </div>
                  </div>
                </div>

                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <div style={{
                    textAlign: "center",
                    fontWeight: 800,
                    marginBottom: 4,
                    color: theme.primary,
                    textShadow: `0 0 10px ${theme.primaryGlow}`,
                  }}>
                    15 min+
                  </div>
                  <div style={{ position: "relative", height: 140, maxWidth: 280, margin: "0 auto" }}>
                    <Pie data={pieLongData} options={{
                      ...pieSimpleOptions,
                      rotation: 0,
                      circumference: 360,
                      cutout: "74%",
                      plugins: {
                        ...pieSimpleOptions.plugins,
                        datalabels: {
                          display: true,
                          color: "#fff",
                          font: { size: 12, weight: "bold" },
                          formatter: (value, ctx) => {
                            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                            if (total === 0) return "0%";
                            const percentage = Math.round((value / total) * 100);
                            return `${percentage}%`;
                          }
                        }
                      }
                    }} />
                  </div>
                  {/* แสดงตัวเลขด้านล่างกราฟ */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginTop: 8,
                    fontSize: "11px",
                    color: theme.textDim
                  }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: theme.success, fontWeight: "bold", fontSize: "14px" }}>
                        {longWinList.length}
                      </div>
                      <div>Win</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: theme.danger, fontWeight: "bold", fontSize: "14px" }}>
                        {longLoseList.length}
                      </div>
                      <div>Loss</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 18, marginTop: 2, color: theme.textDim, fontSize: 12, fontWeight: 700 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", background: theme.success, display: "inline-block" }} />
                  Win
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", background: theme.danger, display: "inline-block" }} />
                  Loss
                </span>
              </div>
            </ChartCard>

            {/* Radar - เต็มแถว */}
            <ChartCard title="Team Performance by Week" minHeight={420} full type="us">
              <div style={{ position: "relative", height: "100%", padding: "6px 10px 4px 10px" }}>
                <Radar data={heroRadarData} options={heroRadarOptions} />
              </div>
            </ChartCard>

            {/* Bar Chart - Win/Loss by Rival */}
            <ChartCard title="Win/Loss by Opponent" minHeight={520} full type="us">
              <div style={{ position: "relative", height: "100%", padding: "8px 10px 4px 10px" }}>
                <Bar
                  data={{
                    labels: rivals,
                    datasets: [
                      {
                        label: "Win",
                        data: rivals.map((r) => winByRival[r]),
                        backgroundColor: theme.success,
                        borderRadius: 6,
                        borderWidth: 1,
                        borderColor: theme.bgCard,
                      },
                      {
                        label: "Loss",
                        data: rivals.map((r) => loseByRival[r]),
                        backgroundColor: theme.danger,
                        borderRadius: 6,
                        borderWidth: 1,
                        borderColor: theme.bgCard,
                      },
                    ],
                  }}
                  options={{
                    ...rivalBarOptions,
                    indexAxis: "y",
                    layout: { padding: { top: 6, right: 8, bottom: 0, left: 0 } },
                    plugins: {
                      ...rivalBarOptions.plugins,
                      tooltip: {
                        ...rivalBarOptions.plugins.tooltip,
                        mode: "index",
                        intersect: false,
                        callbacks: {
                          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw} เกม`,
                        },
                      },
                    },
                  }}
                />
              </div>
            </ChartCard>
          </div>
        </section>

        {/* Table Section */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: theme.bgCard,
            borderRadius: 18,
            boxShadow: theme.shadow,
            padding: "24px 20px 8px 20px",
            margin: "24px auto 32px auto",
            maxWidth: 1100,
            position: "relative",
            zIndex: 2,
            overflow: "hidden",
          }}
        >
          {/* Corner accents for table */}
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 60,
            height: 60,
            borderTop: `3px solid ${theme.primary}`,
            borderLeft: `3px solid ${theme.primary}`,
            borderRadius: "18px 0 0 0",
            pointerEvents: "none",
          }} />

          <div style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 60,
            height: 60,
            borderBottom: `3px solid ${theme.primary}`,
            borderRight: `3px solid ${theme.primary}`,
            borderRadius: "0 0 18px 0",
            pointerEvents: "none",
          }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <h3
              style={{
                color: theme.primary,
                fontWeight: 800,
                letterSpacing: 1,
                fontSize: "1.18rem",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontFamily: "'Orbitron', sans-serif",
                textShadow: `0 0 15px ${theme.primaryGlow}`,
              }}
            >
              <span>{icons.chart}</span>
              ข้อมูลการแข่งขันล่าสุด ({filteredGames.length} รายการ)
            </h3>
            {filteredGames.length > rowsPerPage && (
              <div
                style={{
                  color: theme.primary,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  background: theme.primarySoft,
                  padding: "6px 12px",
                  borderRadius: 12,
                  border: `1px solid ${theme.border}`,
                }}
              >
                Showing {Math.min(rowsPerPage, filteredGames.length)} of {filteredGames.length} matches
              </div>
            )}
          </div>
          <div
            style={{
              maxHeight: 460,
              overflowY: "auto",
              borderRadius: 12,
              boxShadow: "inset 0 0 20px rgba(0,0,0,0.3)",
              position: "relative",
              border: `1px solid ${theme.borderDim}`,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "950px",
                background: theme.bgCard,
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>สัปดาห์</th>
                  <th style={thStyle}>วันที่</th>
                  <th style={thStyle}>เวลา</th>
                  <th style={thStyle}>คู่แข่ง</th>
                  <th style={thStyle}>Side & Match Win</th>
                  <th style={thStyle}>End Type</th>
                  <th style={thStyle}>Type</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filteredGames.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          textAlign: "center",
                          padding: 22,
                          color: theme.textMuted,
                          fontStyle: "italic",
                        }}
                      >
                        {search.trim()
                          ? "No matches found for your search"
                          : "ยังไม่มีข้อมูล"}
                      </td>
                    </tr>
                  ) : (
                    filteredGames
                      .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                      .map((g, idx) => {
                        const week = getWeekOfGame(g) || "-";
                        const dateStr = formatDateDisplay(g.Date);
                        const time = formatTime(g.time);
                        const comp = g.competitor || "ไม่พบข้อมูล";
                        const matchVal = g.match || g["Match Win"] || "";
                        const mr = getMatchResult(matchVal);
                        const endType = g.endType || g.EndType || "ไม่พบข้อมูล";
                        const type = g.type || g.Type || "ไม่พบข้อมูล";
                        const bg = matchBgColors[idx % matchBgColors.length];

                        return (
                          <motion.tr
                            key={`${g.Date}-${comp}-${idx}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 12 }}
                            transition={{ delay: 0.04 + idx * 0.01 }}
                            style={{
                              background: bg,
                              fontWeight: 500,
                              borderBottom: "1px solid rgba(255,16,38,0.1)",
                              transition: "all 0.19s",
                            }}
                          >
                            <td style={tdStyle}>{week}</td>
                            <td style={tdStyle}>{dateStr}</td>
                            <td style={tdStyle}>{time}</td>
                            <td style={tdStyle}>{comp}</td>
                            <td
                              style={{
                                ...tdStyle,
                                fontWeight: 700,
                                color: mr.color,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                              }}
                            >
                              <div style={{
                                color: theme.primary,
                                fontWeight: 800,
                                textShadow: `0 0 8px ${theme.primaryGlow}`,
                              }}>
                                {g.side || g.Side || "ไม่พบข้อมูล"}
                              </div>
                              <div
                                style={{
                                  color: mr.color,
                                  fontWeight: 900,
                                  fontSize: "1.06em",
                                  textShadow: `0 0 10px ${mr.color === theme.success ? theme.successGlow : theme.dangerGlow}`,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "1.1em",
                                    marginRight: 2,
                                  }}
                                >
                                  {mr.icon}
                                </span>
                                {mr.label}
                              </div>
                            </td>
                            <td style={tdStyle}>{endType}</td>
                            <td style={tdStyle}>{type}</td>
                          </motion.tr>
                        );
                      })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredGames.length > rowsPerPage && (
            <motion.div
              style={{
                marginTop: 14,
                display: "flex",
                gap: 8,
                justifyContent: "center",
                alignItems: "center",
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PageBtn onClick={() => setPage(1)} disabled={page === 1} aria-label="First page">
                ⏪
              </PageBtn>
              <PageBtn
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Previous page"
              >
                ◀️
              </PageBtn>
              <span
                style={{
                  fontWeight: 700,
                  color: theme.primary,
                  fontSize: "1.07rem",
                  letterSpacing: 1,
                  background: theme.primarySoft,
                  borderRadius: 6,
                  padding: "3px 13px",
                  minWidth: 90,
                  textAlign: "center",
                  border: `1px solid ${theme.border}`,
                }}
              >
                {page} / {Math.ceil(filteredGames.length / rowsPerPage)}
              </span>
              <PageBtn
                onClick={() => setPage(Math.min(Math.ceil(filteredGames.length / rowsPerPage), page + 1))}
                disabled={page === Math.ceil(filteredGames.length / rowsPerPage)}
                aria-label="Next page"
              >
                ▶️
              </PageBtn>
              <PageBtn
                onClick={() => setPage(Math.ceil(filteredGames.length / rowsPerPage))}
                disabled={page === Math.ceil(filteredGames.length / rowsPerPage)}
                aria-label="Last page"
              >
                ⏩
              </PageBtn>
            </motion.div>
          )}
        </motion.div>

        {/* Footer */}
        <footer
          style={{
            textAlign: "center",
            padding: "50px 20px",
            color: theme.textMuted,
            fontSize: 13,
            position: "relative",
            zIndex: 2,
            borderTop: `1px solid ${theme.borderDim}`,
            marginTop: 40,
          }}
        >
          <div style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: "1.2rem",
            fontWeight: 900,
            color: theme.primary,
            letterSpacing: "4px",
            marginBottom: "8px",
            textShadow: `0 0 20px ${theme.primaryGlow}`,
          }}>
            TO BE NUMBER ONE ESPORTS
          </div>
          <p>Analytics Dashboard • ROV Summer 2026 Draft System</p>
        </footer>
      </div>

    </>
  );
}

// Summary Card component ในธีม TO BE NUMBER ONE ESPORTS
function SummaryCard({ icon, label, value, color, gradient }) {
  return (
    <motion.div
      whileHover={{ scale: 1.03, boxShadow: `0 0 16px ${color}33` }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 200, damping: 12 }}
      style={{
        background: theme.bgCard,
        borderRadius: 18,
        boxShadow: theme.shadow,
        padding: "18px 14px 15px 14px",
        minWidth: 170,
        minHeight: 78,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: `1.5px solid ${color}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: gradient,
          opacity: 0.1,
          zIndex: 0,
        }}
      />

      {/* Corner accents */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 30,
        height: 30,
        borderTop: `2px solid ${color}`,
        borderLeft: `2px solid ${color}`,
        borderRadius: "18px 0 0 0",
      }} />

      <div style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        width: 30,
        height: 30,
        borderBottom: `2px solid ${color}`,
        borderRight: `2px solid ${color}`,
        borderRadius: "0 0 18px 0",
      }} />

      <div style={{
        fontSize: "1.8rem",
        marginBottom: 6,
        textShadow: `0 2px 8px ${color}66`,
        zIndex: 1
      }}>
        {icon}
      </div>
      <div style={{
        color: color,
        fontWeight: 900,
        fontSize: "1.25rem",
        letterSpacing: 1,
        zIndex: 1,
        textShadow: `0 0 8px ${color}66`,
        fontFamily: "'Orbitron', sans-serif",
      }}>
        {value}
      </div>
      <div style={{
        color: theme.textDim,
        fontWeight: 700,
        fontSize: "1rem",
        opacity: 0.85,
        zIndex: 1,
        marginTop: 4,
      }}>
        {label}
      </div>
    </motion.div>
  );
}

// Filter Tag component
function FilterTag({ label, active, onClick, color, icon }) {
  return (
    <motion.button
      whileHover={{ scale: 1.13, boxShadow: `0 0 10px ${color}88` }}
      whileTap={{ scale: 0.94 }}
      onClick={onClick}
      style={{
        background: active ? color : theme.primarySoft,
        color: active ? theme.bg : theme.primary,
        fontWeight: 900,
        borderRadius: 16,
        padding: "8px 21px",
        margin: "2px 0",
        fontSize: "1.07rem",
        cursor: "pointer",
        outline: "none",
        boxShadow: active ? `0 0 15px ${color}44` : undefined,
        transition: "all 0.17s",
        display: "flex",
        alignItems: "center",
        gap: 6,
        border: active ? `2px solid ${color}` : `2px solid ${theme.border}`,
        fontFamily: "'Kanit', sans-serif",
      }}
    >
      {icon && <span style={{ fontSize: "1.1em", filter: active ? "brightness(1.2)" : undefined }}>{icon}</span>}
      {label}
    </motion.button>
  );
}

// Pagination button component
function PageBtn({ onClick, disabled, children, "aria-label": ariaLabel }) {
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: theme.primarySoft,
        color: theme.primary,
        border: `1px solid ${theme.border}`,
        borderRadius: 7,
        padding: "5px 11px",
        fontWeight: 700,
        fontSize: "1.1rem",
        margin: "0 2px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "all .18s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 40,
        minHeight: 40,
      }}
      whileHover={!disabled ? { scale: 1.1, background: "rgba(255,16,38,0.2)" } : {}}
      whileTap={!disabled ? { scale: 0.9 } : {}}
      aria-label={ariaLabel}
    >
      {children}
    </motion.button>
  );
}
